import { ScanResult } from './martech-scanner-constants';
import {
  CLIENT_TYPES,
  JourneyMilestone,
  JourneyTemplate,
  LIBRARY,
  STAGES,
  TEMPLATES,
  VERTICALS
} from './journey-data';
import {
  CANONICAL_RULES,
  CAP_MORE,
  CAPABILITIES,
  CATEGORY_RULES,
  CONSENT_CRITICAL,
  DetectedTool,
  EditableMilestone,
  FOUNDATION_GROUPS,
  FOUNDATION_MORE,
  FoundationItem,
  FoundationObservation,
  JourneyLibraryEntry,
  JourneyModel,
  JourneyRecommendation,
  JourneyStageResult,
  JourneySuggestion,
  MILESTONE_RULES,
  MilestoneStatus,
  MilestoneStatusType,
  PIXELS_LABEL,
  RedundancyFinding,
  STAGE_PRIORITY,
  SUGGESTION_RULES,
  TOOL_RULES,
  TYPE_TO_JT
} from './journey-mapper-constants';

const classifyTool = (name: string, category: string): string | null => {
  const override = TOOL_RULES.find(([pattern]) => pattern.test(name));
  if (override) return override[1];
  if (
    category === 'Platform / CMS' &&
    !/shopify|woocommerce|magento|adobe commerce|bigcommerce|salesforce commerce|sfcc/i.test(name)
  ) {
    return null;
  }
  return CATEGORY_RULES[category] ?? null;
};

const canonicalName = (name: string, category: string): string => {
  const rule = CANONICAL_RULES.find(([cat, pattern]) => category === cat && pattern.test(name));
  return rule ? rule[2] : name;
};

const detectedTools = (result: ScanResult): DetectedTool[] => {
  const tools: DetectedTool[] = [];
  const seen = new Set<string>();

  Object.entries(result.detections).forEach(([category, names]) => {
    Object.keys(names).forEach(name => {
      const cap = classifyTool(name, category);
      if (!cap) return;
      const canon = canonicalName(name, category);
      if (!seen.has(canon)) {
        seen.add(canon);
        tools.push({ name: canon, category, cap });
      }
    });
  });

  return tools;
};

/** Returns (requires_any_of, soft) for a milestone. */
const milestoneRequires = (name: string, signal = ''): { requires: string[]; soft: boolean } => {
  const text = `${name} | ${signal}`.toLowerCase();
  for (const [pattern, caps, soft] of MILESTONE_RULES) {
    if (pattern.test(text)) return { requires: caps, soft };
  }
  return { requires: [], soft: false }; // unknown -> discovery, never a false gap
};

const libByName = new Map(LIBRARY.map(m => [m.name, m]));

const evidenceText = (result: ScanResult): string => {
  const parts: string[] = [];
  Object.values(result.detections).forEach(names => parts.push(...Object.keys(names)));
  parts.push(...(result.runtimeNetwork?.thirdPartyHosts ?? []));
  parts.push(...(result.runtimeNetwork?.firstPartyHosts ?? []));
  parts.push(...Object.keys(result.extractedIds));
  return parts.join('\n');
};

/** Library milestones the audit evidence argues for, not already in the journey. */
const buildSuggestions = (result: ScanResult, currentNames: Set<string>): JourneySuggestion[] => {
  const text = evidenceText(result);
  const out: JourneySuggestion[] = [];
  const seen = new Set<string>();

  SUGGESTION_RULES.forEach(([pattern, names, reasonTemplate]) => {
    const match = text.match(pattern);
    if (!match) return;
    const why = reasonTemplate.replace('{m}', match[0].trim().slice(0, 60));
    names.forEach(name => {
      if (currentNames.has(name.toLowerCase()) || seen.has(name)) return;
      seen.add(name);
      const lib = libByName.get(name);
      if (!lib) return;
      const more = [lib.notes, lib.when ? `When to use: ${lib.when}` : '']
        .filter(Boolean)
        .join(' ');
      out.push({
        name,
        stage: lib.stage,
        stageName: STAGES[lib.stage],
        section: lib.section,
        signal: lib.signal,
        purpose: lib.purpose,
        reason: why,
        more
      });
    });
  });

  return out.sort((a, b) => a.stage - b.stage || a.section.localeCompare(b.section));
};

/**
 * Per the Vertical Map: the vertical's JV tab when the selected client type
 * matches its default; otherwise the JT archetype tab for the type (incl.
 * "Other / Non-standard" verticals).
 */
const pickTemplate = (
  clientType: string,
  vertical: string
): { tab: string; template: JourneyTemplate } => {
  const v = VERTICALS[vertical] ?? VERTICALS.other;
  if (v.tab) {
    const t = TEMPLATES[v.tab];
    if ((t.clientType ?? v.defaultType) === clientType) {
      return { tab: v.tab, template: t };
    }
  }
  const tab = TYPE_TO_JT[clientType] ?? 'JT eComm';
  return { tab, template: TEMPLATES[tab] };
};

const defaultMilestones = (
  clientType: string,
  vertical: string
): { tab: string; milestones: JourneyMilestone[] } => {
  const { tab, template } = pickTemplate(clientType, vertical);
  return { tab, milestones: template.milestones.map(m => ({ ...m })) };
};

const sanitizeMilestones = (milestones: EditableMilestone[]): EditableMilestone[] => {
  const out: EditableMilestone[] = [];
  milestones.forEach(m => {
    const stage = Number(m.stage);
    const name = String(m.name ?? '')
      .trim()
      .slice(0, 120);
    if (stage in STAGES && name) {
      out.push({
        stage,
        name,
        signal: String(m.signal ?? '')
          .trim()
          .slice(0, 120),
        purpose: String(m.purpose ?? '')
          .trim()
          .slice(0, 300),
        notes: String(m.notes ?? '')
          .trim()
          .slice(0, 400)
      });
    }
  });
  return out;
};

const buildRecommendations = (stages: JourneyStageResult[]): JourneyRecommendation[] => {
  const recs: JourneyRecommendation[] = [];
  [...stages]
    .sort((a, b) => STAGE_PRIORITY[a.num] - STAGE_PRIORITY[b.num])
    .forEach(s => {
      s.milestones
        .filter(m => m.status === MilestoneStatus.GAP)
        .forEach(m => {
          const need = m.requires.map(c => CAPABILITIES[c].label).join(' or ');
          recs.push({
            severity: 'GAP',
            stage: s.name,
            milestone: m.name,
            title: `'${m.name}' (${s.name}) has no supporting platform`,
            note: `This milestone needs ${need} — nothing detected in the stack provides it. ${m.purpose}`.trim(),
            more: CAP_MORE[m.requires[0]] ?? ''
          });
        });
    });
  return recs;
};

/** Stack-level findings from the foundation layer, deduped elsewhere against scanner insights. */
const buildFoundationObservations = (
  foundation: FoundationItem[],
  vertical: string
): FoundationObservation[] => {
  const fmap = new Map(foundation.map(f => [f.cap, f]));
  const out: FoundationObservation[] = [];

  if (!fmap.get('consent')?.present) {
    const severe = CONSENT_CRITICAL.has(vertical);
    out.push({
      severity: severe ? 'RISK' : 'GAP',
      title: 'No consent management platform detected',
      note: `Every journey milestone collects data without a detected CMP. ${
        severe
          ? 'In this vertical that is a compliance risk to resolve before media scales.'
          : 'Privacy posture should be confirmed during onboarding.'
      }`,
      more: FOUNDATION_MORE.consent
    });
  }
  if (!fmap.get('cdp')?.present) {
    out.push({
      severity: 'OPPORTUNITY',
      title: 'No CDP detected — milestones run on siloed data',
      note:
        "Audiences built at one milestone can't follow the user to the next. A CDP (or warehouse-native " +
        'equivalent) would connect acquisition signals to lifecycle and loyalty.',
      more: FOUNDATION_MORE.cdp
    });
  }
  if (!(fmap.get('attribution')?.present ?? true)) {
    out.push({
      severity: 'OPPORTUNITY',
      title: 'No attribution / measurement platform detected',
      note:
        'No MMP or ecommerce attribution platform was observed. Cross-channel performance is being read ' +
        "from each ad platform's self-graded homework.",
      more:
        'Every ad platform over-credits itself. An independent attribution layer (MMP for app, or platforms ' +
        'like Northbeam/Rockerbox for web) is how budget decisions get made on one consistent scorecard ' +
        'instead of competing claims.'
    });
  }
  if (!(fmap.get('identity')?.present ?? true)) {
    out.push({
      severity: 'OPPORTUNITY',
      title: 'No first-party data signals in use',
      note:
        'No hashed-email matching (Enhanced Conversions, Advanced Matching), identity resolution, or ' +
        'onsite identity capture was observed feeding the ad platforms.',
      more:
        'As cookies and device IDs decay, hashed first-party data is what keeps match rates and audience ' +
        'quality up. Enhanced Conversions and Meta Advanced Matching are usually the fastest wins — the ' +
        "client already owns the emails; they're just not being put to work."
    });
  }
  if (!fmap.get('sst')?.present) {
    out.push({
      severity: 'OPPORTUNITY',
      title: 'No server-side tagging signals',
      note:
        'Conversion milestones are exposed to browser signal loss. Server-side collection would harden ' +
        'measurement at every paid stage.',
      more: FOUNDATION_MORE.sst
    });
  }

  return out;
};

const buildRedundancies = (tools: DetectedTool[]): RedundancyFinding[] => {
  const byCap = new Map<string, string[]>();
  tools.forEach(t => {
    const list = byCap.get(t.cap) ?? [];
    list.push(t.name);
    byCap.set(t.cap, list);
  });

  const out: RedundancyFinding[] = [];
  byCap.forEach((rawNames, cap) => {
    let names = rawNames;
    if (cap === 'tag_mgmt') {
      // gtag.js is Google's loader, not a competing tag manager — coexisting with GTM is standard, not overlap.
      names = names.filter(n => !/gtag/i.test(n));
    }
    const threshold = ['tag_mgmt', 'cdp', 'testing_perso'].includes(cap) ? 2 : 3;
    if (names.length >= threshold && cap !== 'paid_media' && cap !== 'payments') {
      out.push({
        cap,
        label: CAPABILITIES[cap].label,
        tools: [...names].sort(),
        note:
          `${names.length} platforms provide ${CAPABILITIES[cap].label.toLowerCase()} — likely overlap in ` +
          'cost, data, or page weight. Confirm which is the system of record.',
        more:
          'Overlapping platforms typically mean duplicate subscription spend, double-fired or conflicting ' +
          'events, extra page weight, and a customer identity split across systems. Three questions settle ' +
          'it in discovery: which platform is the system of record, which integrations depend on each one, ' +
          'and what data or workflows would be lost by consolidating. Sometimes the answer is a deliberate ' +
          'migration in progress — worth confirming before recommending a teardown.'
      });
    }
  });

  return out.sort((a, b) => b.tools.length - a.tools.length);
};

/**
 * Core alignment. `editedMilestones` — an optional user-edited milestone
 * list so customizations survive recompute and flow into exports.
 */
export const buildJourney = (
  result: ScanResult,
  clientType = 'ecomm',
  vertical = 'other',
  editedMilestones?: EditableMilestone[],
  clientName = ''
): JourneyModel => {
  const resolvedClientType = clientType in CLIENT_TYPES ? clientType : 'ecomm';
  const resolvedVertical = vertical in VERTICALS ? vertical : 'other';

  const picked = defaultMilestones(resolvedClientType, resolvedVertical);
  let { tab } = picked;
  let base: EditableMilestone[] = picked.milestones;
  // DEV-24211: branch on the *sanitized* result, not the raw input length -
  // an edit list that's non-empty but entirely invalid (e.g. every milestone
  // name blanked) used to sanitize down to zero milestones while still being
  // marked "(customized)", producing an empty stage and inflating the score
  // denominator instead of falling back to the template.
  const sanitized = editedMilestones ? sanitizeMilestones(editedMilestones) : [];
  if (sanitized.length > 0) {
    base = sanitized;
    tab = `${tab} (customized)`;
  }

  const tools = detectedTools(result);
  const byCap = new Map<string, string[]>();
  tools.forEach(t => {
    const list = byCap.get(t.cap) ?? [];
    list.push(t.name);
    byCap.set(t.cap, list);
  });

  const stageNums = Object.keys(STAGES).map(Number);
  const stagesByNum = new Map<number, JourneyStageResult>(
    stageNums.map(n => [
      n,
      { num: n, name: STAGES[n], milestones: [], covered: 0, detectable: 0, discovery: 0 }
    ])
  );

  let no = 0;
  base.forEach(m => {
    const { stage } = m;
    if (!stagesByNum.has(stage) || !m.name) return;
    no += 1;
    const { requires, soft } = milestoneRequires(m.name, m.signal ?? '');
    const supporting = Array.from(new Set(requires.flatMap(c => byCap.get(c) ?? []))).sort();
    let status: MilestoneStatusType = MilestoneStatus.GAP;
    if (supporting.length > 0) {
      status = MilestoneStatus.COVERED;
    } else if (requires.length === 0 || soft) {
      status = MilestoneStatus.DISCOVERY;
    }
    stagesByNum.get(stage)!.milestones.push({
      no,
      stage,
      name: m.name,
      purpose: m.purpose ?? '',
      signal: m.signal ?? '',
      notes: m.notes ?? '',
      requires,
      status,
      tools: supporting
    });
  });

  let totCov = 0;
  let totDet = 0;
  const outStages: JourneyStageResult[] = stageNums.map(n => {
    const s = stagesByNum.get(n)!;
    const covered = s.milestones.filter(m => m.status === MilestoneStatus.COVERED).length;
    const detectable = s.milestones.filter(
      m => m.status === MilestoneStatus.COVERED || m.status === MilestoneStatus.GAP
    ).length;
    const discovery = s.milestones.length - detectable;
    totCov += covered;
    totDet += detectable;
    return { ...s, covered, detectable, discovery };
  });

  const foundation: FoundationItem[] = [];
  Object.entries(FOUNDATION_GROUPS).forEach(([group, g]) => {
    g.caps.forEach(cap => {
      if (cap === 'pixels') {
        const pixelTools = [...(byCap.get('paid_media') ?? [])].sort();
        foundation.push({
          cap: 'pixels',
          label: PIXELS_LABEL,
          group,
          present: pixelTools.length > 0,
          tools: pixelTools
        });
      } else {
        const capTools = [...(byCap.get(cap) ?? [])].sort();
        foundation.push({
          cap,
          label: CAPABILITIES[cap].label,
          group,
          present: capTools.length > 0,
          tools: capTools
        });
      }
    });
  });

  const currentNames = new Set(outStages.flatMap(s => s.milestones.map(m => m.name.toLowerCase())));

  const library: JourneyLibraryEntry[] = LIBRARY.map(m => ({
    section: m.section,
    stage: m.stage,
    name: m.name,
    signal: m.signal,
    purpose: m.purpose
  }));

  return {
    clientName,
    domain: result.domain ?? '',
    clientType: resolvedClientType,
    clientTypeLabel: CLIENT_TYPES[resolvedClientType],
    vertical: resolvedVertical,
    verticalLabel: VERTICALS[resolvedVertical].label,
    templateUsed: tab,
    stages: outStages,
    foundation,
    foundationGroups: Object.fromEntries(
      Object.entries(FOUNDATION_GROUPS).map(([k, v]) => [k, v.label])
    ),
    tools,
    suggestions: buildSuggestions(result, currentNames),
    recommendations: buildRecommendations(outStages),
    foundationObservations: buildFoundationObservations(foundation, resolvedVertical),
    redundancies: buildRedundancies(tools),
    score: {
      covered: totCov,
      detectable: totDet,
      pct: totDet ? Math.round((100 * totCov) / totDet) : 0
    },
    capabilities: Object.fromEntries(Object.entries(CAPABILITIES).map(([k, v]) => [k, v.label])),
    library
  };
};

const STATUS_MARK: Record<string, string> = { covered: '✓', gap: '△', discovery: '○' };

/** Markdown rendering of a journey, appended to the exported brief. */
export const renderJourneyMarkdown = (journey: JourneyModel): string => {
  const lines: string[] = [];
  const w = (line: string) => lines.push(line);
  const sc = journey.score;

  w('\n## User journey alignment');
  const who = journey.clientName || journey.domain;
  const ctx = `${who ? `${who} — ` : ''}${journey.clientTypeLabel} · ${journey.verticalLabel}`;
  w(
    `_${ctx}_ · template: ${journey.templateUsed} · **${sc.covered}/${sc.detectable} detectable milestones supported (${sc.pct}%)**\n`
  );

  journey.stages.forEach(s => {
    const disc = s.discovery ? ` · ${s.discovery} confirm in discovery` : '';
    w(`### ${s.num}. ${s.name} — ${s.covered}/${s.detectable} detectable${disc}`);
    s.milestones.forEach(m => {
      const sig = m.signal ? ` · signal: \`${m.signal}\`` : '';
      if (m.status === MilestoneStatus.COVERED) {
        w(`- ${STATUS_MARK.covered} **${m.name}**${sig} — ${m.tools.join(', ')}`);
      } else if (m.status === MilestoneStatus.GAP) {
        const need = m.requires.map(c => CAPABILITIES[c].label).join(' or ');
        w(`- ${STATUS_MARK.gap} **GAP: ${m.name}**${sig} — needs ${need}`);
      } else {
        w(`- ${STATUS_MARK.discovery} ${m.name}${sig} — confirm in discovery`);
      }
    });
    w('');
  });

  w('### Foundation layers (support every milestone)');
  Object.entries(journey.foundationGroups).forEach(([gkey, glabel]) => {
    const items = journey.foundation.filter(f => f.group === gkey);
    if (items.length === 0) return;
    const gp = items.filter(f => f.present).length;
    w(`#### ${glabel} — ${gp}/${items.length} present`);
    items.forEach(f => {
      const mark = f.present ? '✓' : '—';
      const detail = f.tools.length > 0 ? f.tools.join(', ') : 'not detected';
      w(`- ${mark} ${f.label}: ${detail}`);
    });
    w('');
  });
  if (journey.foundationObservations.length > 0) {
    w('#### Foundation observations');
    journey.foundationObservations.forEach(o => {
      w(`- **[${o.severity}] ${o.title}** — ${o.note}`);
      if (o.more) {
        w(`  - _Why it matters: ${o.more}_`);
      }
    });
    w('');
  }

  if (journey.suggestions.length > 0 || journey.recommendations.length > 0) {
    w('### Journey recommendations');
  }
  if (journey.recommendations.length > 0) {
    w('#### Prioritized gaps & risks (revenue-nearest first)');
    journey.recommendations.forEach(r => {
      w(`- **[${r.severity}] ${r.title}** — ${r.note}`);
      if (r.more) {
        w(`  - _Why it matters: ${r.more}_`);
      }
    });
    w('');
  }
  if (journey.suggestions.length > 0) {
    w('#### Suggested non-standard milestones (evidence-based)');
    w(
      "_The audit found platforms whose journeys the standard template doesn't cover. From the Non-standard Milestone Library:_"
    );
    journey.suggestions.forEach(s => {
      w(
        `- **${s.name}** (stage ${s.stage} · ${s.stageName} · ${s.section}) — ${s.reason}. Signal: \`${s.signal}\``
      );
      if (s.more) {
        w(`  - _${s.more}_`);
      }
    });
    w('');
  }

  if (journey.redundancies.length > 0) {
    w('### Consolidation candidates');
    journey.redundancies.forEach(r => {
      w(`- **${r.label}** — ${r.tools.join(', ')}`);
      if (r.more) {
        w(`  - _${r.more}_`);
      }
    });
    w('');
  }

  w(
    '_✓ a detected platform can power/measure the milestone · △ detectable but nothing in the stack provides ' +
      'it · ○ not observable in a client-side scan (CRM, calls, offline, in-app) — confirm during discovery._'
  );

  return lines.join('\n');
};
